import { useState } from "react";
import {
  Card,
  Button,
  Badge,
  Modal,
  FormLayout,
  Select,
  TextField,
  Banner,
  TextContainer,
  Divider,
  ButtonGroup,
  BlockStack,
  InlineStack,
} from "@shopify/polaris";
import type { CampaignVariant, Game, RewardPolicy } from "../../api/campaigns";
import { isShopInstallRedirectError } from "../../api/admin-api-client";
import { GameConfigForm } from "../GameConfigForm/GameConfigForm";
import { SimpleWheelEditor } from "../SimpleWheel/SimpleWheelEditor";
import { ScratchVariantEditor } from "../ScratchCard/ScratchVariantEditor";
import type { WheelCatalogTemplateId } from "../games/wheelCatalogTemplates";
import { fetchWheelTemplateDefaults } from "../../api/wheel-templates";
import { SCRATCH_VARIANT_HELP } from "../games/scratchProductCopy";

interface Props {
  campaignId: string;
  variants: CampaignVariant[];
  games: Game[];
  rewardPolicies: RewardPolicy[];
  /** From Games catalog / campaign URL — preselect game in Add Variant modal. */
  preselectedGameId?: string | null;
  /** When set with a wheel game, first new variant gets template wheelConfig defaults. */
  initialWheelTemplate?: WheelCatalogTemplateId | null;
  /** Clear template query from URL after a templated wheel variant is created. */
  onConsumedInitialWheelTemplate?: () => void;
  onAddVariant: (variant: {
    gameId: string;
    gameVersionId: string;
    rewardPolicyId: string;
    weight: number;
    config: any;
    wheelConfig?: unknown;
  }) => Promise<void>;
  onUpdateVariant: (
    variantId: string,
    updates: {
      weight?: number;
      config?: any;
      status?: "ACTIVE" | "DISABLED";
      rewardPolicyId?: string;
    }
  ) => Promise<void>;
  onDeleteVariant: (variantId: string) => Promise<void>;
  /** Reload campaign after wheel config save */
  onCampaignReload?: () => Promise<void>;
}

export function VariantManager({
  variants,
  games,
  rewardPolicies,
  preselectedGameId,
  initialWheelTemplate,
  onConsumedInitialWheelTemplate,
  onAddVariant,
  onUpdateVariant,
  onDeleteVariant,
  onCampaignReload,
}: Props) {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  // New variant form state
  const [selectedGameId, setSelectedGameId] = useState<string>("");
  const [selectedGameVersionId, setSelectedGameVersionId] = useState<string>("");
  const [selectedRewardPolicyId, setSelectedRewardPolicyId] = useState<string>("");
  const [variantWeight, setVariantWeight] = useState<number>(100);
  const [variantConfig, setVariantConfig] = useState<any>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rewardPolicySavingVariantId, setRewardPolicySavingVariantId] = useState<string | null>(
    null
  );

  const selectedGame = games.find((g) => g.id === selectedGameId);
  const selectedGameVersion = selectedGame?.versions?.find(
    (v) => v.id === selectedGameVersionId
  );

  const handleOpenAddModal = () => {
    const fromUrl = preselectedGameId
      ? games.find((g) => g.id === preselectedGameId)
      : undefined;
    const initialGame = fromUrl ?? games[0];
    setSelectedGameId(initialGame?.id || "");
    setSelectedGameVersionId(initialGame?.versions?.[0]?.id || "");
    setSelectedRewardPolicyId(rewardPolicies[0]?.id || "");
    setVariantWeight(100);
    setVariantConfig({});
    setError(null);
    setIsAddModalOpen(true);
  };

  const handleCloseAddModal = () => {
    setIsAddModalOpen(false);
    setError(null);
  };

  const handleGameChange = (gameId: string) => {
    setSelectedGameId(gameId);
    const game = games.find((g) => g.id === gameId);
    setSelectedGameVersionId(game?.versions?.[0]?.id || "");
    setVariantConfig({});
  };

  const handleGameVersionChange = (versionId: string) => {
    setSelectedGameVersionId(versionId);
    setVariantConfig({});
  };

  const handleAddVariant = async () => {
    if (!selectedGameId || !selectedGameVersionId || !selectedRewardPolicyId) {
      setError("Please select a game, version, and reward policy");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const useWheelTemplate =
        selectedGame?.key === "wheel_of_fortune" && initialWheelTemplate != null;
      const wheelConfig = useWheelTemplate
        ? (await fetchWheelTemplateDefaults(initialWheelTemplate)).wheelConfig
        : undefined;

      await onAddVariant({
        gameId: selectedGameId,
        gameVersionId: selectedGameVersionId,
        rewardPolicyId: selectedRewardPolicyId,
        weight: variantWeight,
        config: variantConfig,
        ...(wheelConfig !== undefined && { wheelConfig }),
      });
      if (useWheelTemplate) {
        onConsumedInitialWheelTemplate?.();
      }
      handleCloseAddModal();
    } catch (err) {
      if (isShopInstallRedirectError(err)) {
        return;
      }
      setError(err instanceof Error ? err.message : "Failed to add variant");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRewardPolicyChange = async (variant: CampaignVariant, newPolicyId: string) => {
    if (variant.rewardPolicyId === newPolicyId) {
      return;
    }
    setRewardPolicySavingVariantId(variant.id);
    setError(null);
    try {
      await onUpdateVariant(variant.id, { rewardPolicyId: newPolicyId });
    } catch (err) {
      if (isShopInstallRedirectError(err)) {
        return;
      }
      setError(err instanceof Error ? err.message : "Failed to update reward policy");
    } finally {
      setRewardPolicySavingVariantId(null);
    }
  };

  const handleToggleVariantStatus = async (variant: CampaignVariant) => {
    const newStatus = variant.status === "ACTIVE" ? "DISABLED" : "ACTIVE";
    try {
      await onUpdateVariant(variant.id, { status: newStatus });
    } catch (err) {
      if (isShopInstallRedirectError(err)) {
        return;
      }
      setError(err instanceof Error ? err.message : "Failed to update variant status");
    }
  };

  const handleDeleteVariant = async (variantId: string) => {
    if (!confirm("Are you sure you want to delete this variant?")) {
      return;
    }

    try {
      await onDeleteVariant(variantId);
    } catch (err) {
      if (isShopInstallRedirectError(err)) {
        return;
      }
      setError(err instanceof Error ? err.message : "Failed to delete variant");
    }
  };

  const getStatusBadge = (status: "ACTIVE" | "DISABLED") => {
    return status === "ACTIVE" ? (
      <Badge tone="success">Active</Badge>
    ) : (
      <Badge>Disabled</Badge>
    );
  };

  return (
    <Card>
      <div style={{ padding: "1rem" }}>
        <p style={{ fontWeight: 600, marginBottom: "1rem" }}>Campaign Variants</p>
        <TextContainer>
          <p>
            Variants let you run different games or reward policies in the same campaign.
            <strong> Weight</strong> is the relative share of traffic each <em>enabled</em> variant
            receives (for example 70 vs 30 means about 70% vs 30%). The app picks one variant per
            storefront request using these weights. You can change which reward policy a variant uses
            without recreating the variant; that only updates this variant&apos;s link to a policy,
            not the policy definition.
          </p>
        </TextContainer>

        {error && (
          <div style={{ marginTop: "1rem" }}>
            <Banner tone="critical" onDismiss={() => setError(null)}>
              {error}
            </Banner>
          </div>
        )}

        <Divider />

        <div style={{ marginTop: "1.5rem" }}>
          {variants.length === 0 ? (
            <Banner>
              No variants added yet. Add at least one variant to activate this campaign.
            </Banner>
          ) : (
            <BlockStack gap="400">
              {variants.map((variant) => (
                <Card key={variant.id}>
                  <div style={{ padding: "1rem" }}>
                  <InlineStack align="space-between" blockAlign="center">
                    <BlockStack gap="200">
                      <InlineStack gap="200">
                        <strong>{variant.game?.name || "Unknown Game"}</strong>
                        <Badge>{variant.gameVersion?.version || "Unknown Version"}</Badge>
                        {getStatusBadge(variant.status)}
                      </InlineStack>
                      <div style={{ maxWidth: "28rem" }}>
                        <Select
                          label="Reward policy (this variant only)"
                          helpText="Select which policy drives outcomes for this variant only; it does not change the policy definition. For wheel games, segment outcome keys must match the selected policy."
                          options={rewardPolicies.map((p) => ({
                            label: p.name,
                            value: p.id,
                          }))}
                          value={variant.rewardPolicyId}
                          onChange={(v) => handleRewardPolicyChange(variant, v)}
                          disabled={rewardPolicySavingVariantId === variant.id}
                        />
                      </div>
                      <p style={{ color: "#6d7175", fontSize: "0.875rem" }}>
                        Weight: {variant.weight}
                      </p>
                    </BlockStack>
                    <ButtonGroup>
                      <Button
                        size="slim"
                        onClick={() => handleToggleVariantStatus(variant)}
                      >
                        {variant.status === "ACTIVE" ? "Disable" : "Enable"}
                      </Button>
                      <Button
                        size="slim"
                        tone="critical"
                        onClick={() => handleDeleteVariant(variant.id)}
                      >
                        Delete
                      </Button>
                    </ButtonGroup>
                  </InlineStack>
                  {variant.game?.key === "wheel_of_fortune" && (
                    <div style={{ marginTop: "1.25rem" }}>
                      <Divider />
                      <div style={{ marginTop: "1rem" }}>
                        <TextContainer>
                          <p style={{ fontWeight: 600, marginBottom: "0.5rem" }}>Wheel</p>
                          <p style={{ fontSize: "0.875rem", color: "#6d7175" }}>
                            Stored in <code>wheelConfig</code> (separate from legacy game <code>config</code>).
                          </p>
                        </TextContainer>
                        <div style={{ marginTop: "1rem" }}>
                          <SimpleWheelEditor
                            variantId={variant.id}
                            wheelConfig={variant.wheelConfig}
                            onSaved={onCampaignReload}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  {variant.game?.key === "scratch_card" && (
                    <div style={{ marginTop: "1.25rem" }}>
                      <Divider />
                      <div style={{ marginTop: "1rem" }}>
                        <TextContainer>
                          <p style={{ fontWeight: 600, marginBottom: "0.5rem" }}>Scratch card</p>
                          <p style={{ fontSize: "0.875rem", color: "#6d7175" }}>
                            CTA and finish live in variant <code>config</code> and apply on the storefront.
                          </p>
                        </TextContainer>
                        <div style={{ marginTop: "1rem" }}>
                          <ScratchVariantEditor
                            variant={variant}
                            onUpdateVariant={onUpdateVariant}
                            onSaved={onCampaignReload}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  </div>
                </Card>
              ))}
            </BlockStack>
          )}
        </div>

        <div style={{ marginTop: "1.5rem" }}>
          <Button onClick={handleOpenAddModal} disabled={games.length === 0 || rewardPolicies.length === 0}>
            Add Variant
          </Button>
          {games.length === 0 && (
            <p style={{ marginTop: "0.5rem", color: "#bf0711", fontSize: "0.875rem" }}>
              No games available. Please seed the database with games first.
            </p>
          )}
          {rewardPolicies.length === 0 && (
            <p style={{ marginTop: "0.5rem", color: "#bf0711", fontSize: "0.875rem" }}>
              No reward policies found. Create a reward policy first.
            </p>
          )}
        </div>
      </div>

      <Modal
        open={isAddModalOpen}
        onClose={handleCloseAddModal}
        title="Add Campaign Variant"
        primaryAction={{
          content: "Add Variant",
          onAction: handleAddVariant,
          loading: isSubmitting,
        }}
        secondaryActions={[
          {
            content: "Cancel",
            onAction: handleCloseAddModal,
          },
        ]}
      >
        <Modal.Section>
          {error && (
            <Banner tone="critical" onDismiss={() => setError(null)}>
              {error}
            </Banner>
          )}

          {initialWheelTemplate && selectedGame?.key === "wheel_of_fortune" && (
            <div style={{ marginBottom: "1rem" }}>
              <Banner tone="info">
                <p>
                  <strong>Wheel template:</strong>{" "}
                  {initialWheelTemplate === "lead_capture"
                    ? "Lead Capture — contact before spin"
                    : "Instant Discount — spin first, discount outcomes"}
                  . Defaults are saved with this variant; you can edit segments afterward.
                </p>
              </Banner>
            </div>
          )}

          {selectedGame?.key === "scratch_card" && (
            <div style={{ marginBottom: "1rem" }}>
              <Banner tone="info">
                <p>
                  <strong>Scratch card:</strong> {SCRATCH_VARIANT_HELP} You can edit CTA and finish after
                  saving the variant.
                </p>
              </Banner>
            </div>
          )}

          <FormLayout>
            <Select
              label="Game"
              value={selectedGameId}
              options={games.map((game) => ({ label: game.name, value: game.id }))}
              onChange={handleGameChange}
            />

            {selectedGame && selectedGame.versions && selectedGame.versions.length > 0 && (
              <Select
                label="Game Version"
                value={selectedGameVersionId}
                options={selectedGame.versions.map((version) => ({
                  label: `${version.version} - ${version.status}`,
                  value: version.id,
                }))}
                onChange={handleGameVersionChange}
              />
            )}

            <Select
              label="Reward Policy"
              value={selectedRewardPolicyId}
              options={rewardPolicies.map((policy) => ({
                label: policy.name,
                value: policy.id,
              }))}
              onChange={setSelectedRewardPolicyId}
            />

            <TextField
              label="Weight"
              type="number"
              value={variantWeight.toString()}
              onChange={(value) => setVariantWeight(parseInt(value) || 100)}
              helpText="Relative traffic share vs other enabled variants (not a percentage). Must be a positive number; weights of 0 or invalid values are treated as 1 at runtime."
              min={1}
              autoComplete="off"
            />
          </FormLayout>

          {selectedGameVersion && selectedGameVersion.configSchema && (
            <div style={{ marginTop: "1.5rem" }}>
              <GameConfigForm
                schema={selectedGameVersion.configSchema}
                initialConfig={variantConfig}
                onChange={setVariantConfig}
              />
            </div>
          )}
        </Modal.Section>
      </Modal>
    </Card>
  );
}
